import VenueCardNoImg from "@/components/views/list/card/VenueCardNoImg";
import S3Image from "@/components/S3Image";
import { Button } from "@/components/ui/button";
import Squares2X2Icon from "@heroicons/react/24/outline/Squares2X2Icon";
import { useState } from "react";
import { GLOBAL_VENUE_DISCOUNT_PERCENT_KEY } from "@/@core/constants/discount";
import { useTranslation } from "@/hooks/translationContext";
import { cn } from "@/lib/utils";

interface Actions {
  setActiveImage: (index: number) => void;
  setShow: (data: boolean) => void;
  setShowGallery: (data: boolean) => void;
}

interface Props extends Actions {
  venue: any;
}

export default function VenueDetailHeaderPhotoSection(props: Props) {
  const { venue, setActiveImage, setShow, setShowGallery } = props;
  const { t } = useTranslation();
  const [notFound, setNotFound] = useState([false, false, false, false, false]);

  const getVenuePhotoByIndex = (index: number) => {
    if (!venue.banners?.[index]?.url) return null;

    return `/image/venue/${venue.id}/photo/${venue.banners[index]?.url}?w=700`;
  };

  const globalDiscountPercent = t(GLOBAL_VENUE_DISCOUNT_PERCENT_KEY);

  const hasDiscount = () => {
    const activeDiscounts = venue?.discounts?.find(d => d?.percent == 0);
    if (activeDiscounts) return false;

    return (
      venue.discounts.length ||
      globalDiscountPercent !== GLOBAL_VENUE_DISCOUNT_PERCENT_KEY
    );
  };

  const borderRadiusClasses = {
    0: "rounded-l-[32px]",
    2: "rounded-tr-[32px]"
  };

  const getBorderRadiusClass = (index) => borderRadiusClasses[index] || "";

  const imageData = [
    {
      index: 0,
      width: 608,
      height: 456,
      className: "w-full h-[calc(100dvh-300px)] max-h-[456px]",
    },
    {
      index: 1,
      width: 296,
      height: 220,
      className: "h-[calc((100dvh-300px)/2.07)] max-h-[220px]",
    },
    {
      index: 2,
      width: 296,
      height: 220,
      className:
        "h-[calc((100dvh-300px)/2.07)] max-h-[220px]",
    },
    {
      index: 3,
      width: 296,
      height: 220,
      className: "h-[calc((100dvh-300px)/2.07)] max-h-[220px]",
    },
    {
      index: 4,
      width: 296,
      height: 220,
      className: "h-[calc((100dvh-300px)/2.07)] max-h-[220px]",
    },
  ];

  const renderImage = (data: (typeof imageData)[0]) => (
    <>
      {!getVenuePhotoByIndex(data.index) || notFound[data.index] ? (
        <div
          className={cn("bg-assistant/30 h-full w-full", getBorderRadiusClass(data.index))}
        >
          <VenueCardNoImg key={data.index} type={0} />
        </div>
      ) : (
        <button
          key={data.index}
          className="w-full"
          onClick={() => {
            setActiveImage(data.index);
            setShow(true);
          }}
        >
          <S3Image
            width={data.width}
            height={data.height}
            priority={data.index <= 1}
            unoptimized
            src={getVenuePhotoByIndex(data.index)}
            alt={t("venue-detail-photo-alt", {
              venue: venue?.title,
              index: data.index + 1,
            })}
            title={t("venue-detail-photo-alt", {
              venue: venue?.title,
              index: data.index + 1,
            })}
            className={cn("object-cover object-center", getBorderRadiusClass(data.index), data.className, {
              "min-h-[300px]": data.index === 0,
              "min-h-[142px]": data.index > 0,
            })}
            onError={() =>
              setNotFound((prev) => {
                const updated = [...prev];
                updated[data.index] = true;
                return updated;
              })
            }
          />
        </button>
      )}
    </>
  );

  return (
    <div className="md:flex hidden h-[calc(100dvh-300px)] max-h-[456px] min-h-[300px] overflow-hidden">
      <div className="gap-4 relative md:flex hidden h-full w-full">
        <div className="w-1/2">
          {renderImage(imageData[0])}
        </div>

        <div className="flex flex-col w-1/2 gap-4 h-full">
          <div className="flex gap-4 h-full">
            {renderImage(imageData[1])}
            {renderImage(imageData[2])}
          </div>

          <div className="flex gap-4 h-full">
            {renderImage(imageData[3])}
            {renderImage(imageData[4])}
          </div>
        </div>

        {/* {props.datas.discounts && props.datas.discounts?.length > 0 && ( */}
        {hasDiscount() && (
          <div className="absolute left-6 top-6 text-[10px] bg-white rounded-full px-2 py-1 text-assistant">
            <span>
              {t("venue-card-discount-text", {
                discount: venue.discounts.length
                  ? venue.discounts[0]?.percent
                  : globalDiscountPercent,
              })}
            </span>
          </div>
        )}
        {/* )} */}

        {venue.banners.length > 5 && (
          <Button
            className="absolute bottom-6 right-6 bg-white hover:bg-slate-100 text-[10px] py-[10px] px-4 rounded-full"
            onClick={() => setShowGallery(true)}
            variant="primary"
          >
            <Squares2X2Icon className="size-4" />
            Tüm Fotoğrafları Göster
            <div className="w-6 h-4 flex justify-center items-center bg-primary rounded-full text-white">
              +{venue.banners.length - 5}
            </div>
          </Button>
        )}
      </div>
    </div>
  );
}
